import { useQuery, useQueryClient } from '@tanstack/react-query'
import { compact, take } from 'lodash'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ComputeFactory } from '../../../models/ComputeFactory'
import { SubaccountType } from '../../../models/Subaccount'
import { Subaccount } from '../../../models/Subaccount'
import { User } from '../../../models/User'
import { Spinner } from '../../Spinner'
import Button from '../../app/design/Button'
import Card from '../../app/design/Card'
import Label from '../../app/design/Label'
import Modal from '../../app/design/Modal'
import Paragraph from '../../app/design/Paragraph'
import { ContactAvatar } from '../../contacts/ContactAvatar'
import { NewSubaccountPage } from '../../contacts/NewSubaccountPage'
import { BaseRecordView } from './'

type Props = {
    user: User
    industrySubaccountType: SubaccountType
    owner: User
}

export const SubaccountsSection = ({ user, industrySubaccountType, owner }: Props) => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()

    const { data: subaccounts } = useQuery({
        queryKey: ['subaccounts', user],
        queryFn: () => ComputeFactory.getInstance().fetchSubaccounts(user),
    })

    const [isListModalOpen, setIsListModalOpen] = useState(false)
    const [isNewSubaccountModalOpen, setIsNewSubaccountModalOpen] = useState(false)

    const handleAddSubaccount = () => {
        setIsListModalOpen(false)
        setIsNewSubaccountModalOpen(true)
    }

    return (
        <Card
            size="sm"
            title={Subaccount.labelString(industrySubaccountType, true)}
            count={
                subaccounts !== undefined && subaccounts.length > 0 ? subaccounts.length : undefined
            }
            headerButtons={compact([
                subaccounts !== undefined &&
                    subaccounts.length > 3 && {
                        title: 'See all',
                        onClick: () => setIsListModalOpen(true),
                    },
                { title: 'Add', onClick: handleAddSubaccount },
            ])}
        >
            {subaccounts !== undefined && subaccounts.length > 0 && (
                <div className="mt-6 flex flex-col items-stretch justify-start gap-y-8">
                    {subaccounts !== undefined ? (
                        take(subaccounts, 3).map((subaccount) => (
                            <BaseRecordView.IconField
                                key={subaccount.id}
                                icon={<ContactAvatar user={subaccount} />}
                                title={subaccount.name ?? ''}
                                subtitle={subaccount.labelString}
                                href={`/subaccounts/${subaccount.id}`}
                                titleSize="medium"
                                hasHoverEffect
                            />
                        ))
                    ) : (
                        <div className="flex items-center justify-center">
                            <Spinner size={48} />
                        </div>
                    )}
                </div>
            )}

            {subaccounts !== undefined && (
                <Modal
                    isOpen={isListModalOpen}
                    onClose={() => setIsListModalOpen(false)}
                    title={Subaccount.labelString(industrySubaccountType, true)}
                    footer={
                        <Button intent="secondary" onClick={handleAddSubaccount}>
                            Add a{' '}
                            {Subaccount.labelString(industrySubaccountType, false).toLowerCase()}
                        </Button>
                    }
                >
                    <div className="flex flex-col items-stretch justify-start gap-y-3">
                        {subaccounts.map((subaccount) => (
                            <Link
                                key={subaccount.id ?? ''}
                                to={`/subaccounts/${subaccount.id}`}
                                className="flex items-center justify-start gap-x-4 rounded-lg border border-gray-300 p-4 hover:bg-black/4 active:bg-black/8"
                            >
                                <ContactAvatar user={subaccount} />
                                <div className="flex flex-col items-start justify-center gap-y-1">
                                    <Label className="text-gray-700">{subaccount.name}</Label>
                                    <Paragraph size="sm" className="font-normal text-gray-600">
                                        {subaccount.labelString}
                                    </Paragraph>
                                </div>
                            </Link>
                        ))}
                    </div>
                </Modal>
            )}

            {isNewSubaccountModalOpen && (
                <NewSubaccountPage
                    defaultSubaccount={
                        new Subaccount({ owner, type: industrySubaccountType, client: user })
                    }
                    onClose={() => setIsNewSubaccountModalOpen(false)}
                    owner={owner}
                    onComplete={(subaccount) => {
                        queryClient.invalidateQueries({ queryKey: ['subaccounts', user] })
                        navigate(`/subaccounts/${subaccount.id}`)
                    }}
                    canEditClient={false}
                />
            )}
        </Card>
    )
}
